(() => {
  const ELEMENT_NAME = 'toffee-set-builder';

  if (customElements.get(ELEMENT_NAME)) return;

  const normalise = (value = '') => String(value)
    .toLocaleLowerCase('de-CH')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  const asPositiveInteger = (value, fallback = 0) => {
    const number = Number.parseInt(value, 10);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  };

  const priceToCents = (value, fallback = 0) => {
    if (typeof value === 'string' && /[.,]/.test(value)) {
      const decimalValue = Number.parseFloat(value.replace(',', '.'));
      return Number.isFinite(decimalValue) ? Math.max(0, Math.round(decimalValue * 100)) : fallback;
    }

    const cents = Number.parseInt(value, 10);
    return Number.isFinite(cents) ? Math.max(0, cents) : fallback;
  };

  const inventoryCap = (management, policy, quantity) => {
    const parsedQuantity = Number.parseInt(quantity, 10);
    const inventoryIsTracked = Boolean(management) && normalise(policy) !== 'continue';
    return inventoryIsTracked && Number.isFinite(parsedQuantity)
      ? Math.max(0, parsedQuantity)
      : Number.POSITIVE_INFINITY;
  };

  class ToffeeSetBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.currency = this.dataset.currency || 'CHF';
      this.locale = this.dataset.locale || 'de-CH';
      this.collectionCount = asPositiveInteger(this.dataset.collectionCount);
      if (!this.collectionCount) return;
      this.collectionHandle = this.dataset.collectionHandle || '';
      this.sectionId = this.dataset.sectionId || '';
      this.editBundleId = this.readEditBundleId();
      this.prefillSelection = this.readPrefillSelection();
      this.baseSessionKey = this.dataset.sessionKey || `toffee:set-builder:${this.dataset.hostProductId || 'default'}`;
      this.sessionKey = this.editBundleId
        ? `${this.baseSessionKey}:edit:${this.editBundleId}`
        : this.baseSessionKey;
      this.editGroupLoaded = false;
      this.editOriginalSnapshot = [];
      this.selection = new Map();
      this.products = new Map();
      this.eligibleProductIds = new Set();
      this.activeFilters = { size: '', type: '' };
      this.cartEligibleQuantity = 0;
      this.cartQuantityByVariant = new Map();
      this.nextCollectionPage = 2;
      this.hasMoreProducts = false;
      this.loadingProducts = false;
      this.busy = false;
      this.drawerOpen = false;
      this.cartSuppressed = false;
      this.searchTimer = null;

      this.cacheElements();
      this.registerRenderedProducts();
      this.updateFilterAvailability();
      if (!this.editBundleId) this.restoreSession();
      this.bindEvents();
      this.setupStickyBuilder();
      this.setupCartDrawerObserver();
      this.syncCards();
      this.render();
      if (this.editBundleId) {
        this.loadEditedBundle();
      } else {
        this.loadCartEligibleQuantity();
        this.applyPrefillSelection();
      }
    }

    disconnectedCallback() {
      this.intersectionObserver?.disconnect();
      this.cartDrawerObserver?.disconnect();
      window.clearTimeout(this.searchTimer);
      window.clearTimeout(this.cartSuppressionTimer);
      document.documentElement.removeEventListener('cart:change', this.boundCartChange);
      document.documentElement.classList.remove('toffee-set-builder-dialog-open');
    }

    cacheElements() {
      this.panel = this.querySelector('.toffee-set-builder__panel');
      this.dialog = this.querySelector('[data-builder-dialog]');
      this.grid = this.querySelector('[data-builder-grid]');
      this.searchInput = this.querySelector('[data-builder-search]');
      this.emptyState = this.querySelector('[data-builder-empty]');
      this.loadMoreButton = this.querySelector('[data-builder-load-more]');
      this.loadingLabel = this.querySelector('[data-builder-loading]');
      this.resultCount = this.querySelector('[data-builder-result-count]');
      this.liveRegion = this.querySelector('[data-builder-live]');
      this.errorElement = this.querySelector('[data-builder-error]');
      this.selectionBlock = this.querySelector('[data-builder-selection]');
      this.selectionRail = this.querySelector('[data-builder-selection-rail]');
      this.slotRail = this.querySelector('[data-builder-slot-rail]');
      this.drawerSelection = this.querySelector('[data-builder-drawer-selection]');
      this.drawerThumbnails = this.querySelector('[data-builder-drawer-thumbnails]');
      this.sticky = this.querySelector('[data-builder-sticky]');
      this.primaryButtons = Array.from(this.querySelectorAll('[data-builder-primary], [data-builder-sticky-action]'));
    }

    readEditBundleId() {
      try {
        const value = new URL(window.location.href).searchParams.get('toffee_bundle_edit') || '';
        const bundleId = value.trim();
        return bundleId.length <= 240 && /^[A-Za-z0-9:_-]+$/.test(bundleId) ? bundleId : '';
      } catch (error) {
        return '';
      }
    }

    readPrefillSelection() {
      if (this.editBundleId) return null;

      try {
        const url = new URL(window.location.href);
        const variantId = asPositiveInteger(url.searchParams.get('toffee_bundle_variant'));
        const handle = (url.searchParams.get('toffee_bundle_product') || '').trim();

        if (!variantId || !/^[a-z0-9-]{1,160}$/.test(handle)) return null;
        return { variantId, handle };
      } catch (error) {
        return null;
      }
    }

    async applyPrefillSelection() {
      const prefill = this.prefillSelection;
      if (!prefill || this.editBundleId) return;

      const productData = this.products.get(prefill.variantId);
      if (!productData || productData.handle !== prefill.handle || !this.eligibleProductIds.has(productData.productId)) {
        this.showError('Dieses Produkt gehört nicht zur aktuellen Set-Auswahl.');
      } else if (productData.available && !this.selection.has(prefill.variantId)) {
        this.selection.set(prefill.variantId, { ...productData, quantity: 1 });
        this.saveSession();
        this.syncCard(prefill.variantId);
        this.render();
        this.live(`${productData.title} ist bereits für dein Set ausgewählt.`);
      }

      this.clearPrefillParameters();
      this.prefillSelection = null;
    }

    clearPrefillParameters() {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('toffee_bundle_variant');
        url.searchParams.delete('toffee_bundle_product');
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        // Selection is already persisted even if the URL cannot be cleaned.
      }
    }

    bindEvents() {
      this.addEventListener('click', (event) => this.handleClick(event));
      this.searchInput?.addEventListener('input', () => {
        window.clearTimeout(this.searchTimer);
        this.searchTimer = window.setTimeout(() => this.applyFilters(), 100);
      });

      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeDrawer();
      });

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeDrawer();
      });

      this.dialog?.addEventListener('close', () => this.cleanupClosedDrawer());

      this.boundCartChange = (event) => {
        if (event.detail?.cart) {
          this.updateCartEligibleQuantity(event.detail.cart);
        } else {
          this.loadCartEligibleQuantity();
        }
      };
      document.documentElement.addEventListener('cart:change', this.boundCartChange);
    }

    handleClick(event) {
      const openButton = event.target.closest('[data-builder-open]');
      if (openButton) {
        this.openDrawer(openButton);
        return;
      }

      if (event.target.closest('[data-builder-close]')) {
        this.closeDrawer();
        return;
      }

      if (event.target.closest('[data-builder-apply]')) {
        this.closeDrawer();
        this.live(`Auswahl mit ${this.totalQuantity()} ${this.totalQuantity() === 1 ? 'Produkt' : 'Produkten'} übernommen.`);
        return;
      }

      const filterButton = event.target.closest('[data-filter-value]');
      if (filterButton) {
        this.selectFilter(filterButton);
        return;
      }

      const imageSelectButton = event.target.closest('[data-builder-image-select]');
      if (imageSelectButton) {
        const card = imageSelectButton.closest('[data-builder-card]');
        this.changeQuantity(card?.dataset.variantId, 1);
        return;
      }

      const incrementButton = event.target.closest('[data-builder-increment]');
      if (incrementButton) {
        const card = incrementButton.closest('[data-builder-card]');
        this.changeQuantity(card?.dataset.variantId, 1);
        return;
      }

      const decrementButton = event.target.closest('[data-builder-decrement]');
      if (decrementButton) {
        const card = decrementButton.closest('[data-builder-card]');
        this.changeQuantity(card?.dataset.variantId, -1);
        return;
      }

      const selectedItem = event.target.closest('[data-builder-selected-item]');
      if (selectedItem) {
        this.openDrawer(selectedItem);
        window.requestAnimationFrame(() => {
          const card = this.grid?.querySelector(`[data-variant-id="${CSS.escape(selectedItem.dataset.variantId || '')}"]`);
          card?.scrollIntoView({ block: 'center', behavior: this.reducedMotion() ? 'auto' : 'smooth' });
        });
        return;
      }

      if (event.target.closest('[data-builder-load-more]')) {
        this.loadNextCollectionPage();
        return;
      }

      if (event.target.closest('[data-builder-primary], [data-builder-sticky-action]')) {
        if (this.totalQuantity() === 0 && !this.editGroupLoaded) {
          this.openDrawer(event.target.closest('button'));
        } else {
          this.addSelectionToCart();
        }
      }
    }

    registerRenderedProducts() {
      this.querySelectorAll('[data-builder-card]').forEach((card) => {
        const productData = this.productFromCard(card);
        if (!productData?.variantId || this.products.has(productData.variantId)) {
          card.remove();
          return;
        }

        this.products.set(productData.variantId, productData);
        this.eligibleProductIds.add(productData.productId);
      });
      const currentPage = asPositiveInteger(this.grid?.dataset.builderCurrentPage, 1);
      const totalPages = asPositiveInteger(this.grid?.dataset.builderTotalPages, 1);
      const pageParam = this.grid?.dataset.builderPageParam || 'page';
      this.paginationParam = /^[A-Za-z0-9_]+$/.test(pageParam) ? pageParam : 'page';
      this.nextCollectionPage = currentPage + 1;
      this.hasMoreProducts = Boolean(this.sectionId) && currentPage < totalPages;
      if (this.loadMoreButton) this.loadMoreButton.hidden = !this.hasMoreProducts;
      if (this.resultCount && this.hasMoreProducts) {
        this.resultCount.textContent = `${this.products.size} von ${this.collectionCount} Produkten`;
      }
    }

    productFromCard(card) {
      const variantId = asPositiveInteger(card.dataset.variantId);
      const productId = asPositiveInteger(card.dataset.productId);
      if (!variantId || !productId) return null;

      const inventoryManagement = card.dataset.inventoryManagement || '';
      const inventoryPolicy = card.dataset.inventoryPolicy || '';
      const inventoryQuantity = Number.parseInt(card.dataset.inventoryQuantity, 10);

      return {
        productId,
        handle: card.dataset.productHandle || '',
        variantId,
        title: card.dataset.title || '',
        variantTitle: card.dataset.variantTitle || '',
        price: priceToCents(card.dataset.price),
        available: card.dataset.available === 'true',
        inventoryManagement,
        inventoryPolicy,
        inventoryQuantity: Number.isFinite(inventoryQuantity) ? inventoryQuantity : null,
        maxQuantity: inventoryCap(inventoryManagement, inventoryPolicy, inventoryQuantity),
        size: normalise(card.dataset.size),
        type: normalise(card.dataset.type),
        image: card.dataset.image || '',
        imageAlt: card.dataset.imageAlt || card.dataset.title || '',
        search: normalise(card.dataset.search || `${card.dataset.title || ''} ${card.dataset.variantTitle || ''}`),
        card
      };
    }

    updateFilterAvailability() {
      this.querySelectorAll('[data-filter-group]').forEach((group) => {
        const filterName = group.dataset.filterGroup;
        const availableValues = new Set(
          Array.from(this.products.values())
            .map((productData) => productData[filterName])
            .filter(Boolean)
        );
        let visibleChoices = 0;

        group.querySelectorAll('[data-filter-value]').forEach((button) => {
          const value = normalise(button.dataset.filterValue);
          if (!value) return;
          button.hidden = !availableValues.has(value);
          if (!button.hidden) visibleChoices += 1;
        });

        const minimumChoices = filterName === 'size' ? 2 : 1;
        group.hidden = visibleChoices < minimumChoices;
      });
    }

    restoreSession() {
      try {
        const saved = JSON.parse(window.sessionStorage.getItem(this.sessionKey));
        if (!saved || saved.version !== 1 || !Array.isArray(saved.items)) return;

        saved.items.forEach((savedItem) => {
          const variantId = asPositiveInteger(savedItem.variantId);
          const quantity = asPositiveInteger(savedItem.quantity);
          if (!variantId || !quantity) return;

          const item = this.products.get(variantId);
          if (!item) return;

          const availableQuantity = this.availableForSelection(item);
          if (availableQuantity <= 0) return;
          this.selection.set(variantId, { ...item, quantity: Math.min(quantity, availableQuantity) });
        });
      } catch (error) {
        try {
          window.sessionStorage.removeItem(this.sessionKey);
        } catch (storageError) {
          // Ignore storage restrictions and continue with an empty in-memory set.
        }
      }
    }

    sanitiseSavedProduct(item) {
      const variantId = asPositiveInteger(item.variantId);
      if (!variantId || typeof item.title !== 'string' || typeof item.handle !== 'string') return null;

      return {
        productId: asPositiveInteger(item.productId),
        handle: item.handle.slice(0, 160),
        variantId,
        title: item.title.slice(0, 240),
        variantTitle: String(item.variantTitle || '').slice(0, 160),
        price: Math.max(0, Number.parseInt(item.price, 10) || 0),
        available: item.available !== false,
        inventoryManagement: String(item.inventoryManagement || ''),
        inventoryPolicy: String(item.inventoryPolicy || ''),
        inventoryQuantity: Number.isFinite(Number(item.inventoryQuantity)) ? Number(item.inventoryQuantity) : null,
        maxQuantity: inventoryCap(item.inventoryManagement, item.inventoryPolicy, item.inventoryQuantity),
        size: normalise(item.size),
        type: normalise(item.type),
        image: typeof item.image === 'string' ? item.image.slice(0, 1200) : '',
        imageAlt: String(item.imageAlt || item.title).slice(0, 300),
        search: normalise(`${item.title} ${item.variantTitle || ''} ${item.size || ''} ${item.type || ''}`),
        card: null
      };
    }

    saveSession() {
      const items = Array.from(this.selection.values()).map((item) => ({
        productId: item.productId,
        handle: item.handle,
        variantId: item.variantId,
        title: item.title,
        variantTitle: item.variantTitle,
        price: item.price,
        available: item.available,
        inventoryManagement: item.inventoryManagement,
        inventoryPolicy: item.inventoryPolicy,
        inventoryQuantity: item.inventoryQuantity,
        size: item.size,
        type: item.type,
        image: item.image,
        imageAlt: item.imageAlt,
        quantity: item.quantity
      }));

      try {
        window.sessionStorage.setItem(this.sessionKey, JSON.stringify({ version: 1, items }));
      } catch (error) {
        // The builder stays functional when storage is blocked by the browser.
      }
    }

    changeQuantity(rawVariantId, delta) {
      const variantId = asPositiveInteger(rawVariantId);
      const productData = this.products.get(variantId) || this.selection.get(variantId);
      if (!variantId || !productData || this.busy) return;

      const currentQuantity = this.selection.get(variantId)?.quantity || 0;
      const maxQuantity = this.availableForSelection(productData);

      if (delta > 0 && currentQuantity >= maxQuantity) {
        this.live(`${productData.title}: Der verfügbare Bestand von ${maxQuantity} Stück ist bereits ausgewählt.`);
        return;
      }

      const nextQuantity = Math.min(maxQuantity, Math.max(0, currentQuantity + delta));

      if (delta > 0 && !productData.available) {
        this.live(`${productData.title} ist aktuell ausverkauft.`);
        return;
      }

      if (nextQuantity === 0) {
        this.selection.delete(variantId);
        this.live(`${productData.title} aus dem Set entfernt.`);
      } else {
        this.selection.set(variantId, { ...productData, quantity: nextQuantity });
        this.live(`${productData.title}: ${nextQuantity} Stück ausgewählt.`);
      }

      this.errorElement.hidden = true;
      this.saveSession();
      this.syncCard(variantId);
      this.render();
    }

    syncCards() {
      this.products.forEach((productData) => this.syncCard(productData.variantId));
    }

    syncCard(variantId) {
      const productData = this.products.get(Number(variantId));
      if (!productData?.card) return;

      const quantity = this.selection.get(Number(variantId))?.quantity || 0;
      const quantityOutput = productData.card.querySelector('[data-builder-quantity]');
      const decrement = productData.card.querySelector('[data-builder-decrement]');
      const increment = productData.card.querySelector('[data-builder-increment]');
      const imageSelect = productData.card.querySelector('[data-builder-image-select]');
      const availableQuantity = this.availableForSelection(productData);
      const selectionDisabled = !productData.available
        || this.busy
        || quantity >= availableQuantity;

      if (quantityOutput) quantityOutput.textContent = String(quantity);
      if (decrement) decrement.disabled = quantity === 0 || this.busy;
      if (increment) increment.disabled = selectionDisabled;
      if (imageSelect) imageSelect.disabled = selectionDisabled;
      productData.card.classList.toggle('is-selected', quantity > 0);
    }

    availableForSelection(productData) {
      if (!Number.isFinite(productData?.maxQuantity)) return Number.POSITIVE_INFINITY;
      const quantityAlreadyInCart = this.cartQuantityByVariant?.get(productData.variantId) || 0;
      return Math.max(0, productData.maxQuantity - quantityAlreadyInCart);
    }

    totalQuantity() {
      return Array.from(this.selection.values()).reduce((total, item) => total + item.quantity, 0);
    }

    qualifyingSelectionQuantity() {
      const eligibleProductIds = this.eligibleProductIds || new Set();
      return Array.from(this.selection.values()).reduce((total, item) => (
        eligibleProductIds.has(item.productId) ? total + item.quantity : total
      ), 0);
    }

    qualifyingQuantity() {
      return this.cartEligibleQuantity + this.qualifyingSelectionQuantity();
    }

    subtotal() {
      return Array.from(this.selection.values()).reduce((total, item) => total + (item.price * item.quantity), 0);
    }

    discountableSubtotal() {
      const eligibleProductIds = this.eligibleProductIds || new Set();
      return Array.from(this.selection.values()).reduce((total, item) => (
        eligibleProductIds.has(item.productId) ? total + (item.price * item.quantity) : total
      ), 0);
    }

    discountPercentage(quantity = this.qualifyingQuantity()) {
      if (quantity >= 5) return 20;
      if (quantity >= 3) return 10;
      return 0;
    }

    tierCopy(quantity = this.qualifyingQuantity()) {
      if (quantity === 0) return 'Wähle deine ersten Favoriten';
      if (quantity === 1) return 'Noch 2 Artikel aus der Auswahl bis 10 %';
      if (quantity === 2) return 'Noch 1 Artikel aus der Auswahl bis 10 %';
      if (quantity === 3) return '10 % Rabattstufe · noch 2 Artikel bis 20 %';
      if (quantity === 4) return '10 % Rabattstufe · noch 1 Artikel bis 20 %';
      return '20 % Rabattstufe für die Auswahl im Warenkorb';
    }

    render() {
      const quantity = this.totalQuantity();
      const qualifyingQuantity = this.qualifyingQuantity();
      const subtotal = this.subtotal();
      const discountableSubtotal = this.discountableSubtotal();
      const discount = this.discountPercentage(qualifyingQuantity);
      const savings = Math.round(discountableSubtotal * discount / 100);
      const estimated = Math.max(0, subtotal - savings);
      const progress = Math.min(100, qualifyingQuantity * 20);
      const countLabel = `${quantity} ${quantity === 1 ? 'Produkt' : 'Produkte'}`;
      const tierCopy = this.tierCopy(qualifyingQuantity);
      const nextThreshold = qualifyingQuantity < 3 ? 3 : qualifyingQuantity < 5 ? 5 : 0;

      this.dataset.builderState = quantity === 0
        ? 'empty'
        : discount === 20
          ? 'tier-20'
          : discount === 10
            ? 'tier-10'
            : 'building';
      this.dataset.builderQuantity = String(quantity);
      this.dataset.qualifyingQuantity = String(qualifyingQuantity);
      this.dataset.discount = String(discount);

      this.querySelectorAll('[data-builder-progress-fill]').forEach((element) => {
        element.style.width = `${progress}%`;
      });
      this.querySelectorAll('[data-builder-progress]').forEach((element) => {
        element.setAttribute('aria-valuenow', String(Math.min(5, qualifyingQuantity)));
        element.setAttribute('aria-valuetext', tierCopy);
      });
      this.querySelectorAll('[data-builder-progress-copy]').forEach((element) => {
        element.textContent = tierCopy;
      });
      this.querySelectorAll('[data-builder-progress-step]').forEach((element) => {
        const threshold = asPositiveInteger(element.dataset.builderProgressStep);
        const complete = qualifyingQuantity >= threshold;
        const isNext = qualifyingQuantity < 5 && threshold === qualifyingQuantity + 1;
        element.classList.toggle('is-complete', complete);
        element.classList.toggle('is-next', isNext);
      });
      this.querySelectorAll('[data-tier-marker], [data-tier-label]').forEach((element) => {
        const threshold = asPositiveInteger(element.dataset.tierMarker || element.dataset.tierLabel);
        element.classList.toggle('is-active', qualifyingQuantity >= threshold);
      });
      this.querySelectorAll('[data-builder-tier-card]').forEach((card) => {
        const threshold = asPositiveInteger(card.dataset.builderTierCard);
        const complete = qualifyingQuantity >= threshold;
        const isNext = nextThreshold === threshold;
        card.classList.toggle('is-complete', complete);
        card.classList.toggle('is-next', isNext);
        if (isNext) card.setAttribute('aria-current', 'step');
        else card.removeAttribute('aria-current');

        const status = card.querySelector('[data-builder-tier-card-status]');
        if (status) status.textContent = complete ? 'Aktiv' : `Noch ${Math.max(0, threshold - qualifyingQuantity)}`;
      });

      this.querySelectorAll('[data-builder-cart-context]').forEach((element) => {
        element.hidden = this.cartEligibleQuantity === 0;
      });
      this.setText(
        '[data-builder-cart-eligible-count]',
        `${this.cartEligibleQuantity} ${this.cartEligibleQuantity === 1 ? 'berechtigtes Produkt' : 'berechtigte Produkte'}`
      );

      this.setText('[data-builder-subtotal]', this.formatMoney(subtotal));
      this.setText('[data-builder-savings]', `− ${this.formatMoney(savings)}`);
      this.setText('[data-builder-estimated]', this.formatMoney(estimated));
      this.setText('[data-builder-selection-count]', `${quantity} ausgewählt`);
      this.setText('[data-builder-savings-label]', discount > 0 ? `Voraussichtlicher Rabatt (${discount} %)` : 'Voraussichtlicher Rabatt');
      this.setText('[data-builder-drawer-count]', countLabel);
      this.setText('[data-builder-drawer-tier]', tierCopy);
      this.setText('[data-builder-drawer-total]', this.formatMoney(estimated));
      this.setText('[data-builder-sticky-count]', countLabel);
      this.setText('[data-builder-sticky-tier]', tierCopy);
      this.setText('[data-builder-sticky-total]', this.formatMoney(estimated));

      this.querySelectorAll('[data-builder-savings-row]').forEach((row) => {
        row.hidden = savings === 0;
      });
      this.querySelectorAll('[data-builder-summary]').forEach((summary) => {
        summary.hidden = quantity === 0;
      });

      if (this.selectionBlock) this.selectionBlock.hidden = quantity === 0;
      if (this.drawerSelection) this.drawerSelection.hidden = quantity === 0;
      this.renderSelectedRail(this.selectionRail, true);
      this.renderSelectedRail(this.drawerThumbnails, false);
      this.renderSelectionSlots(this.slotRail);

      const primaryLabel = quantity === 0
        ? this.editGroupLoaded
          ? 'Set aus dem Warenkorb entfernen'
          : 'Haarklammern auswählen'
        : this.editGroupLoaded
          ? `Set aktualisieren · ${this.formatMoney(estimated)}`
          : `Set in den Warenkorb · ${this.formatMoney(estimated)}`;

      this.primaryButtons.forEach((button) => {
        if (!this.busy) button.textContent = primaryLabel;
        button.setAttribute('aria-haspopup', quantity === 0 && !this.editGroupLoaded ? 'dialog' : 'false');
      });

      this.querySelectorAll('[data-builder-apply]').forEach((button) => {
        button.textContent = quantity === 0 ? 'Auswahl schliessen' : `Auswahl übernehmen · ${countLabel}`;
      });

      this.syncCards();
      this.updateStickyVisibility();
    }

    renderSelectionSlots(container) {
      if (!container) return;
      const visibleUnits = [];

      this.selection.forEach((item) => {
        for (let index = 0; index < item.quantity && visibleUnits.length < 5; index += 1) {
          visibleUnits.push(item);
        }
      });

      const fragment = document.createDocumentFragment();
      for (let index = 0; index < 5; index += 1) {
        const item = visibleUnits[index];
        const slot = document.createElement('button');
        slot.type = 'button';
        slot.className = `toffee-set-builder__slot${item ? ' is-filled' : ''}`;

        if (item) {
          slot.dataset.builderSelectedItem = '';
          slot.dataset.variantId = String(item.variantId);
          slot.setAttribute('aria-label', `${item.title} – Auswahl bearbeiten`);

          if (item.image) {
            const image = document.createElement('img');
            image.src = this.cdnImage(item.image, 180);
            image.alt = '';
            image.loading = 'lazy';
            image.width = 90;
            image.height = 90;
            slot.append(image);
          }
        } else {
          slot.dataset.builderOpen = '';
          slot.setAttribute('aria-haspopup', 'dialog');
          if (this.dialog?.id) slot.setAttribute('aria-controls', this.dialog.id);
          slot.setAttribute('aria-label', `Produkt für Set-Platz ${index + 1} auswählen`);

          const plus = document.createElement('span');
          plus.className = 'toffee-set-builder__slot-plus';
          plus.textContent = '+';
          plus.setAttribute('aria-hidden', 'true');
          slot.append(plus);
        }

        const number = document.createElement('span');
        number.className = 'toffee-set-builder__slot-number';
        number.textContent = String(index + 1);
        number.setAttribute('aria-hidden', 'true');
        slot.append(number);

        if (index === 2 || index === 4) {
          const milestone = document.createElement('span');
          milestone.className = 'toffee-set-builder__slot-milestone';
          milestone.textContent = index === 2 ? '10 % Vorteil' : '20 % Vorteil';
          milestone.setAttribute('aria-hidden', 'true');
          slot.append(milestone);
        }

        fragment.append(slot);
      }

      container.replaceChildren(fragment);
      const overflow = this.querySelector('[data-builder-slot-overflow]');
      const extraQuantity = Math.max(0, this.totalQuantity() - 5);
      if (overflow) {
        overflow.hidden = extraQuantity === 0;
        overflow.textContent = extraQuantity > 0 ? `+ ${extraQuantity} weitere im Set` : '';
      }
    }

    renderSelectedRail(container, interactive) {
      if (!container) return;
      const fragment = document.createDocumentFragment();

      this.selection.forEach((item) => {
        const element = document.createElement(interactive ? 'button' : 'span');
        if (interactive) {
          element.type = 'button';
          element.dataset.builderSelectedItem = '';
          element.dataset.variantId = String(item.variantId);
          element.setAttribute('aria-label', `${item.title}, ${item.quantity} Stück – Auswahl bearbeiten`);
        }
        element.className = 'toffee-set-builder__selected-item';

        if (item.image) {
          const image = document.createElement('img');
          image.src = this.cdnImage(item.image, 160);
          image.alt = item.imageAlt || item.title;
          image.loading = 'lazy';
          image.width = 80;
          image.height = 80;
          element.append(image);
        }

        const count = document.createElement('span');
        count.className = 'toffee-set-builder__selected-count';
        count.textContent = String(item.quantity);
        element.append(count);
        fragment.append(element);
      });

      container.replaceChildren(fragment);
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((element) => {
        element.textContent = value;
      });
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          currencyDisplay: 'code',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(cents / 100);
      } catch (error) {
        return `${this.currency} ${(cents / 100).toFixed(2)}`;
      }
    }

    selectFilter(button) {
      const group = button.closest('[data-filter-group]');
      const filterName = group?.dataset.filterGroup;
      if (!filterName || !(filterName in this.activeFilters)) return;

      this.activeFilters[filterName] = normalise(button.dataset.filterValue);
      group.querySelectorAll('[data-filter-value]').forEach((candidate) => {
        const active = candidate === button;
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-pressed', String(active));
      });
      this.applyFilters();
    }

    applyFilters() {
      const search = normalise(this.searchInput?.value);
      let visibleCount = 0;

      this.products.forEach((productData) => {
        if (!productData.card) return;
        const matchesSearch = !search || productData.search.includes(search);
        const matchesSize = !this.activeFilters.size || productData.size === this.activeFilters.size;
        const matchesType = !this.activeFilters.type || productData.type === this.activeFilters.type;
        const visible = matchesSearch && matchesSize && matchesType;
        productData.card.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      if (this.resultCount) this.resultCount.textContent = `${visibleCount} ${visibleCount === 1 ? 'Produkt' : 'Produkte'}`;
      if (this.emptyState) this.emptyState.hidden = visibleCount > 0 || this.loadingProducts;
    }

    bundleLines(cart, bundleId = this.editBundleId) {
      if (!bundleId) return [];
      return (cart?.items || []).filter((item) => (
        item?.properties?._toffee_bundle_id === bundleId
        && item?.properties?._toffee_bundle_source === 'set-builder'
      ));
    }

    snapshotBundle(cart, bundleId = this.editBundleId) {
      return this.bundleLines(cart, bundleId).map((item) => ({
        key: String(item.key || ''),
        variantId: asPositiveInteger(item.variant_id || item.id),
        productId: asPositiveInteger(item.product_id),
        quantity: asPositiveInteger(item.quantity),
        handle: String(item.handle || item.product_handle || ''),
        title: String(item.product_title || item.title || ''),
        variantTitle: String(item.variant_title || ''),
        price: priceToCents(item.original_price ?? item.price),
        image: String(item.featured_image?.url || item.image || ''),
        imageAlt: String(item.featured_image?.alt || item.product_title || item.title || ''),
        properties: { ...(item.properties || {}) },
        sellingPlan: item.selling_plan_allocation?.selling_plan?.id || null
      })).filter((item) => item.variantId && item.quantity);
    }

    snapshotQuantities(snapshot) {
      const quantities = new Map();
      (snapshot || []).forEach((item) => {
        quantities.set(item.variantId, (quantities.get(item.variantId) || 0) + item.quantity);
      });
      return quantities;
    }

    quantitiesMatch(firstSnapshot, secondSnapshot) {
      const first = this.snapshotQuantities(firstSnapshot);
      const second = this.snapshotQuantities(secondSnapshot);
      if (first.size !== second.size) return false;
      return Array.from(first.entries()).every(([variantId, quantity]) => second.get(variantId) === quantity);
    }

    strictSnapshotSignature(snapshot) {
      const grouped = new Map();
      (snapshot || []).forEach((item) => {
        const properties = Object.entries(item.properties || {})
          .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey))
          .map(([key, value]) => [key, String(value ?? '')]);
        const identity = JSON.stringify([
          item.variantId,
          item.sellingPlan ? String(item.sellingPlan) : '',
          properties
        ]);
        grouped.set(identity, (grouped.get(identity) || 0) + item.quantity);
      });
      return JSON.stringify(Array.from(grouped.entries()).sort(([first], [second]) => first.localeCompare(second)));
    }

    strictSnapshotsMatch(firstSnapshot, secondSnapshot) {
      return this.strictSnapshotSignature(firstSnapshot) === this.strictSnapshotSignature(secondSnapshot);
    }

    productFromCartLine(line) {
      const currentProduct = this.products.get(line.variantId);
      if (currentProduct) return currentProduct;

      return this.sanitiseSavedProduct({
        productId: line.productId,
        handle: line.handle,
        variantId: line.variantId,
        title: line.title,
        variantTitle: line.variantTitle,
        price: line.price,
        available: true,
        inventoryManagement: '',
        inventoryPolicy: '',
        inventoryQuantity: null,
        size: '',
        type: '',
        image: line.image,
        imageAlt: line.imageAlt
      });
    }

    async loadEditedBundle() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Der Warenkorb konnte nicht geladen werden.');

        const cart = await response.json();
        const snapshot = this.snapshotBundle(cart);
        const hostHandle = this.dataset.hostHandle || '';
        const belongsToHost = snapshot.length > 0 && snapshot.every((line) => (
          !hostHandle || !line.properties?._toffee_bundle_host || line.properties._toffee_bundle_host === hostHandle
        ));

        if (!snapshot.length || !belongsToHost) {
          this.editBundleId = '';
          this.sessionKey = this.baseSessionKey;
          this.updateCartEligibleQuantity(cart);
          this.showError('Dieses Set ist nicht mehr im Warenkorb verfügbar. Du kannst stattdessen ein neues Set zusammenstellen.');
          return;
        }

        this.editOriginalSnapshot = snapshot;
        this.editGroupLoaded = true;
        this.selection.clear();

        const quantities = this.snapshotQuantities(snapshot);
        snapshot.forEach((line) => {
          if (this.selection.has(line.variantId)) return;
          const productData = this.productFromCartLine(line);
          if (!productData) return;
          this.selection.set(line.variantId, {
            ...productData,
            quantity: quantities.get(line.variantId) || line.quantity
          });
        });

        this.updateCartEligibleQuantity(cart);
        await this.refreshSelectedAvailability();
        this.saveSession();
        this.syncCards();
        this.render();
        this.live(`Set mit ${this.totalQuantity()} ${this.totalQuantity() === 1 ? 'Produkt' : 'Produkten'} zum Bearbeiten geladen.`);
      } catch (error) {
        this.showError(error.message || 'Das Set konnte nicht zum Bearbeiten geladen werden.');
        await this.loadCartEligibleQuantity();
      }
    }

    async loadCartEligibleQuantity() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        this.updateCartEligibleQuantity(await response.json());
      } catch (error) {
        // The native discount remains authoritative if the cart preview cannot be loaded.
      }
    }

    updateCartEligibleQuantity(cart) {
      const eligibleProductIds = this.eligibleProductIds || new Set();
      this.cartQuantityByVariant = new Map();
      (cart?.items || []).forEach((item) => {
        if (item?.properties?._toffee_bundle_id === this.editBundleId) return;
        const variantId = asPositiveInteger(item.variant_id || item.id);
        if (!variantId) return;
        this.cartQuantityByVariant.set(
          variantId,
          (this.cartQuantityByVariant.get(variantId) || 0) + (Number(item.quantity) || 0)
        );
      });
      this.cartEligibleQuantity = (cart?.items || []).reduce((total, item) => (
        eligibleProductIds.has(Number(item.product_id))
          && item?.properties?._toffee_bundle_id !== this.editBundleId
          ? total + (Number(item.quantity) || 0)
          : total
      ), 0);
      this.syncCards();
      this.render();
    }

    openDrawer(trigger) {
      if (!this.dialog || this.drawerOpen) return;
      this.returnFocusTarget = trigger instanceof HTMLElement ? trigger : document.activeElement;
      this.drawerOpen = true;
      this.classList.add('is-dialog-open');
      document.documentElement.classList.add('toffee-set-builder-dialog-open');

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      window.requestAnimationFrame(() => {
        this.dialog.classList.add('is-visible');
        const focusDelay = this.reducedMotion() ? 0 : 160;
        window.setTimeout(() => this.searchInput?.focus({ preventScroll: true }), focusDelay);
      });

      this.updateStickyVisibility();
    }

    closeDrawer(immediate = false) {
      if (!this.dialog || !this.drawerOpen) return;
      this.dialog.classList.remove('is-visible');
      const delay = immediate || this.reducedMotion() ? 0 : 220;

      window.setTimeout(() => {
        if (this.dialog.open && typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
          this.cleanupClosedDrawer();
        }
      }, delay);
    }

    cleanupClosedDrawer() {
      this.drawerOpen = false;
      this.classList.remove('is-dialog-open');
      document.documentElement.classList.remove('toffee-set-builder-dialog-open');
      this.updateStickyVisibility();

      if (this.returnFocusTarget?.isConnected) {
        this.returnFocusTarget.focus({ preventScroll: true });
      }
      this.returnFocusTarget = null;
    }

    setupStickyBuilder() {
      if (!this.panel || !this.sticky || !('IntersectionObserver' in window)) return;

      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.panelInView = entry.isIntersecting;
        this.panelPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.updateStickyVisibility();
      }, { threshold: 0.01 });

      this.intersectionObserver.observe(this.panel);
    }

    setupCartDrawerObserver() {
      const cartDrawers = Array.from(document.querySelectorAll('cart-drawer, cart-notification-drawer, [data-cart-drawer]'));
      if (cartDrawers.length === 0 || !('MutationObserver' in window)) return;

      this.cartDrawers = cartDrawers;
      this.cartDrawerObserver = new MutationObserver(() => this.updateStickyVisibility());
      cartDrawers.forEach((drawer) => {
        this.cartDrawerObserver.observe(drawer, {
          attributes: true,
          attributeFilter: ['open', 'class', 'aria-hidden']
        });
      });
    }

    cartDrawerIsOpen() {
      return (this.cartDrawers || []).some((drawer) => (
        drawer.hasAttribute('open')
        || drawer.classList.contains('is-open')
        || drawer.classList.contains('show')
        || drawer.getAttribute('aria-hidden') === 'false'
      ));
    }

    updateStickyVisibility() {
      if (!this.sticky) return;
      this.sticky.hidden = !this.panelPassed || this.drawerOpen || this.cartSuppressed || this.cartDrawerIsOpen();
    }

    async loadNextCollectionPage() {
      if (this.loadingProducts || !this.hasMoreProducts || !this.sectionId) return;
      this.loadingProducts = true;
      if (this.loadingLabel) this.loadingLabel.hidden = false;
      if (this.loadMoreButton) {
        this.loadMoreButton.disabled = true;
        this.loadMoreButton.textContent = 'Wird geladen…';
      }

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', this.sectionId);
        if (this.paginationParam !== 'page') url.searchParams.delete('page');
        url.searchParams.set(this.paginationParam, String(this.nextCollectionPage));

        const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Weitere Produkte konnten nicht geladen werden.');

        const html = await response.text();
        const responseDocument = new DOMParser().parseFromString(html, 'text/html');
        const responseBuilder = Array.from(responseDocument.querySelectorAll('toffee-set-builder')).find(
          (builder) => builder.dataset.hostProductId === (this.dataset.hostProductId || '')
        );
        const responseGrid = responseBuilder?.querySelector('[data-builder-grid]');
        const responsePage = asPositiveInteger(responseGrid?.dataset.builderCurrentPage);
        const responseTotalPages = asPositiveInteger(responseGrid?.dataset.builderTotalPages);
        const responsePageParam = responseGrid?.dataset.builderPageParam || 'page';

        if (
          !responseGrid
          || responsePage !== this.nextCollectionPage
          || !responseTotalPages
          || responsePageParam !== this.paginationParam
        ) {
          throw new Error('Weitere Produkte konnten nicht eindeutig geladen werden.');
        }

        responseGrid.querySelectorAll('[data-builder-card]').forEach((responseCard) => {
          const card = responseCard.cloneNode(true);
          const productData = this.productFromCard(card);
          if (!productData?.variantId || this.products.has(productData.variantId)) return;

          productData.card = card;
          this.products.set(productData.variantId, productData);
          this.eligibleProductIds.add(productData.productId);
          this.grid?.append(card);

          const savedItem = this.selection.get(productData.variantId);
          if (savedItem) {
            this.selection.set(productData.variantId, { ...productData, quantity: savedItem.quantity });
          }
        });

        this.nextCollectionPage = responsePage + 1;
        this.hasMoreProducts = responsePage < responseTotalPages;
        this.applyFilters();
        this.updateFilterAvailability();
        this.syncCards();
        await this.loadCartEligibleQuantity();
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.loadingProducts = false;
        if (this.loadingLabel) this.loadingLabel.hidden = true;
        if (this.loadMoreButton) {
          this.loadMoreButton.hidden = !this.hasMoreProducts;
          this.loadMoreButton.disabled = false;
          this.loadMoreButton.textContent = 'Weitere Produkte laden';
        }
      }
    }

    createCard(productData) {
      const card = document.createElement('article');
      card.className = 'toffee-set-builder__card';
      card.dataset.builderCard = '';
      card.dataset.variantId = String(productData.variantId);
      card.dataset.available = String(productData.available);

      const media = document.createElement('button');
      media.type = 'button';
      media.className = 'toffee-set-builder__card-media';
      media.dataset.builderImageSelect = '';
      media.setAttribute('aria-label', `${productData.title} auswählen`);
      media.disabled = !productData.available;
      if (productData.image) {
        const image = document.createElement('img');
        image.className = 'toffee-set-builder__card-image';
        image.src = this.cdnImage(productData.image, 480);
        image.alt = productData.imageAlt;
        image.loading = 'lazy';
        image.width = 480;
        image.height = 480;
        media.append(image);
      }
      if (!productData.available) {
        const soldOut = document.createElement('span');
        soldOut.className = 'toffee-set-builder__sold-out';
        soldOut.textContent = 'Ausverkauft';
        media.append(soldOut);
      }

      const copy = document.createElement('div');
      copy.className = 'toffee-set-builder__card-copy';
      if (productData.variantTitle) {
        const variant = document.createElement('p');
        variant.className = 'toffee-set-builder__variant-label';
        variant.textContent = productData.variantTitle;
        copy.append(variant);
      }
      const title = document.createElement('h3');
      title.textContent = productData.title;
      const price = document.createElement('p');
      price.className = 'toffee-set-builder__card-price';
      price.textContent = this.formatMoney(productData.price);
      const stock = document.createElement('p');
      stock.className = 'toffee-set-builder__card-stock';
      stock.textContent = productData.available ? 'Auf Lager' : 'Ausverkauft';
      copy.append(title, price, stock);

      const stepper = document.createElement('div');
      stepper.className = 'toffee-set-builder__stepper';
      stepper.dataset.builderStepper = '';
      const decrement = document.createElement('button');
      decrement.type = 'button';
      decrement.dataset.builderDecrement = '';
      decrement.setAttribute('aria-label', `${productData.title} entfernen`);
      decrement.textContent = '−';
      decrement.disabled = true;
      const output = document.createElement('output');
      output.dataset.builderQuantity = '';
      output.setAttribute('aria-label', `Ausgewählte Menge für ${productData.title}`);
      output.textContent = '0';
      const increment = document.createElement('button');
      increment.type = 'button';
      increment.dataset.builderIncrement = '';
      increment.setAttribute('aria-label', `${productData.title} hinzufügen`);
      increment.textContent = '+';
      increment.disabled = !productData.available;
      stepper.append(decrement, output, increment);

      card.append(media, copy, stepper);
      return card;
    }

    cdnImage(source, width) {
      if (!source) return '';
      try {
        const url = new URL(source, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch (error) {
        return source;
      }
    }

    async addSelectionToCart() {
      if (this.busy || (this.totalQuantity() === 0 && !this.editGroupLoaded)) return;
      const selectedItems = Array.from(this.selection.values());
      const ineligibleItem = selectedItems.find((item) => !this.eligibleProductIds?.has(item.productId));
      if (ineligibleItem) {
        this.showError(`${ineligibleItem.title} gehört nicht mehr zur aktuellen Bundle-Auswahl. Bitte lade die Seite neu oder entferne den Artikel im Warenkorb.`);
        return;
      }

      if (this.editGroupLoaded && this.editBundleId) {
        await this.updateEditedBundleInCart(selectedItems);
        return;
      }

      const unavailableItem = selectedItems.find((item) => !item.available);
      if (unavailableItem) {
        this.showError(`${unavailableItem.title} ist aktuell nicht verfügbar. Bitte passe deine Auswahl an.`);
        return;
      }
      const overstockItem = selectedItems.find((item) => (
        Number.isFinite(item.maxQuantity) && item.quantity > this.availableForSelection(item)
      ));
      if (overstockItem) {
        this.showError(`${overstockItem.title}: Für dein neues Set sind aktuell nur ${this.availableForSelection(overstockItem)} Stück verfügbar.`);
        return;
      }

      const bundleId = this.createBundleId();
      const items = selectedItems.map((item) => ({
        id: item.variantId,
        quantity: item.quantity,
        properties: {
          _toffee_bundle_id: bundleId,
          _toffee_bundle_source: 'set-builder',
          _toffee_bundle_host: this.dataset.hostHandle || ''
        }
      }));

      this.setBusy(true);
      this.errorElement.hidden = true;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });
        const responseJson = await response.json();

        if (!response.ok) {
          throw new Error(responseJson.description || responseJson.message || 'Das Set konnte nicht hinzugefügt werden.');
        }

        const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        if (!cartResponse.ok) throw new Error('Der Warenkorb konnte nicht aktualisiert werden.');

        this.selection.clear();
        try {
          window.sessionStorage.removeItem(this.sessionKey);
        } catch (storageError) {
          // Storage access is optional; the successful cart add must not be rolled back.
        }
        this.syncCards();
        this.render();
        this.live('Dein TOFFEE Set wurde zum Warenkorb hinzugefügt.');
        this.suppressStickyForCart();
        this.emitCartEvents(responseJson, cart);

        if (window.themeVariables?.settings?.cartType === 'page') {
          window.location.href = `${root}cart`;
          return;
        }
      } catch (error) {
        const rollbackSucceeded = await this.rollbackBundle(bundleId);
        await this.refreshSelectedAvailability();
        const baseMessage = error.message || 'Das Set konnte nicht hinzugefügt werden. Bitte versuche es erneut.';
        if (!rollbackSucceeded) {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          this.showError(`${baseMessage} Einzelne Artikel könnten bereits hinzugefügt worden sein – bitte prüfe deinen Warenkorb.`);
        } else {
          this.showError(baseMessage);
        }
      } finally {
        this.setBusy(false);
      }
    }

    async updateEditedBundleInCart(selectedItems) {
      const root = window.Shopify?.routes?.root || '/';
      const desiredQuantities = new Map(selectedItems.map((item) => [item.variantId, item.quantity]));
      const originalSnapshot = this.editOriginalSnapshot.map((item) => ({
        ...item,
        properties: { ...(item.properties || {}) }
      }));
      const originalQuantities = this.snapshotQuantities(originalSnapshot);
      let mutationStarted = false;

      this.setBusy(true);
      this.errorElement.hidden = true;

      try {
        await this.refreshSelectedAvailability();

        const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('Der Warenkorb konnte vor der Aktualisierung nicht geprüft werden.');
        const cartBefore = await cartResponse.json();
        const currentSnapshot = this.snapshotBundle(cartBefore);

        if (!currentSnapshot.length || !this.strictSnapshotsMatch(originalSnapshot, currentSnapshot)) {
          throw new Error('Das Set wurde zwischenzeitlich im Warenkorb verändert. Bitte lade die Seite neu, bevor du es bearbeitest.');
        }

        const outsideCartQuantities = new Map();
        (cartBefore.items || []).forEach((item) => {
          if (item?.properties?._toffee_bundle_id === this.editBundleId) return;
          const variantId = asPositiveInteger(item.variant_id || item.id);
          if (!variantId) return;
          outsideCartQuantities.set(
            variantId,
            (outsideCartQuantities.get(variantId) || 0) + (Number(item.quantity) || 0)
          );
        });

        for (const item of selectedItems) {
          const originalQuantity = originalQuantities.get(item.variantId) || 0;
          const addedQuantity = item.quantity - originalQuantity;
          if (addedQuantity <= 0) continue;

          if (!item.available) {
            throw new Error(`${item.title} ist aktuell nicht mehr verfügbar. Deine bestehende Set-Auswahl wurde nicht verändert.`);
          }

          const outsideQuantity = outsideCartQuantities.get(item.variantId) || 0;
          if (Number.isFinite(item.maxQuantity) && item.quantity + outsideQuantity > item.maxQuantity) {
            const availableForSet = Math.max(0, item.maxQuantity - outsideQuantity);
            throw new Error(`${item.title}: Für dieses Set sind aktuell höchstens ${availableForSet} Stück verfügbar.`);
          }
        }

        const positiveAdds = selectedItems.flatMap((item) => {
          const difference = item.quantity - (originalQuantities.get(item.variantId) || 0);
          if (difference <= 0) return [];
          const originalLine = originalSnapshot.find((line) => line.variantId === item.variantId);
          const properties = {
            ...(originalLine?.properties || {}),
            _toffee_bundle_id: this.editBundleId,
            _toffee_bundle_source: 'set-builder',
            _toffee_bundle_host: this.dataset.hostHandle || ''
          };
          const cartItem = { id: item.variantId, quantity: difference, properties };
          if (originalLine?.sellingPlan) cartItem.selling_plan = originalLine.sellingPlan;
          return [cartItem];
        });

        if (positiveAdds.length) {
          mutationStarted = true;
          const addResponse = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ items: positiveAdds })
          });
          const addJson = await addResponse.json();
          if (!addResponse.ok) {
            throw new Error(addJson.description || addJson.message || 'Die neue Auswahl ist nicht vollständig verfügbar.');
          }
        }

        const afterAddResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!afterAddResponse.ok) throw new Error('Der Warenkorb konnte nach der Bestandsprüfung nicht geladen werden.');
        const cartAfterAdds = await afterAddResponse.json();
        const snapshotAfterAdds = this.snapshotBundle(cartAfterAdds);
        const updates = this.buildGroupQuantityUpdates(snapshotAfterAdds, desiredQuantities);

        if (updates.missingVariantIds.length) {
          throw new Error('Mindestens ein ausgewähltes Produkt konnte nicht vollständig vorgemerkt werden.');
        }

        let cartAfterUpdate = cartAfterAdds;
        if (Object.keys(updates.values).length) {
          mutationStarted = true;
          const updateResponse = await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ updates: updates.values })
          });
          const updateJson = await updateResponse.json();
          if (!updateResponse.ok) {
            throw new Error(updateJson.description || updateJson.message || 'Das Set konnte nicht aktualisiert werden.');
          }
          cartAfterUpdate = updateJson;
        }

        const verificationResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!verificationResponse.ok) throw new Error('Die aktualisierte Auswahl konnte nicht bestätigt werden.');
        const verifiedCart = await verificationResponse.json();
        const verifiedSnapshot = this.snapshotBundle(verifiedCart);
        const desiredSnapshot = selectedItems.map((item) => ({ variantId: item.variantId, quantity: item.quantity }));

        if (!this.quantitiesMatch(verifiedSnapshot, desiredSnapshot)) {
          throw new Error('Die aktualisierte Auswahl stimmt nicht vollständig mit deinem Set überein.');
        }

        const groupWasRemoved = verifiedSnapshot.length === 0;
        this.editOriginalSnapshot = verifiedSnapshot;
        this.updateCartEligibleQuantity(verifiedCart);
        if (groupWasRemoved) {
          this.clearEditMode();
        } else {
          this.saveSession();
        }
        this.render();
        this.live(groupWasRemoved ? 'Dein TOFFEE Set wurde aus dem Warenkorb entfernt.' : 'Dein TOFFEE Set wurde aktualisiert.');
        this.suppressStickyForCart();
        this.emitCartUpdateEvents(verifiedCart);

        if (window.themeVariables?.settings?.cartType === 'page') {
          window.location.href = `${root}cart`;
          return;
        }
      } catch (error) {
        const rollbackSucceeded = !mutationStarted || await this.restoreGroupSnapshot(originalSnapshot);
        const baseMessage = error.message || 'Das Set konnte nicht aktualisiert werden. Bitte versuche es erneut.';
        if (!rollbackSucceeded) {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          this.showError(`${baseMessage} Der ursprüngliche Stand konnte nicht vollständig wiederhergestellt werden – bitte prüfe deinen Warenkorb.`);
        } else {
          this.showError(baseMessage);
        }
      } finally {
        this.setBusy(false);
      }
    }

    clearEditMode() {
      const editSessionKey = this.sessionKey;
      this.editGroupLoaded = false;
      this.editBundleId = '';
      this.editOriginalSnapshot = [];
      this.sessionKey = this.baseSessionKey;

      try {
        window.sessionStorage.removeItem(editSessionKey);
      } catch (error) {
        // Storage is optional and must not affect the successful cart update.
      }

      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('toffee_bundle_edit');
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        // The cart is already correct even when browser URL APIs are restricted.
      }
    }

    buildGroupQuantityUpdates(snapshot, desiredQuantities) {
      const linesByVariant = new Map();
      (snapshot || []).forEach((line) => {
        if (!linesByVariant.has(line.variantId)) linesByVariant.set(line.variantId, []);
        linesByVariant.get(line.variantId).push(line);
      });

      const values = {};
      linesByVariant.forEach((lines, variantId) => {
        const targetQuantity = desiredQuantities.get(variantId) || 0;
        lines.forEach((line, index) => {
          const lineTarget = index === 0 ? targetQuantity : 0;
          if (line.key && line.quantity !== lineTarget) values[line.key] = lineTarget;
        });
      });

      const missingVariantIds = Array.from(desiredQuantities.entries())
        .filter(([variantId, quantity]) => quantity > 0 && !linesByVariant.has(variantId))
        .map(([variantId]) => variantId);

      return { values, missingVariantIds };
    }

    async restoreGroupSnapshot(originalSnapshot) {
      try {
        const root = window.Shopify?.routes?.root || '/';
        let cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) return false;
        let cart = await cartResponse.json();
        let currentSnapshot = this.snapshotBundle(cart);
        const originalQuantities = this.snapshotQuantities(originalSnapshot);
        const currentQuantities = this.snapshotQuantities(currentSnapshot);

        const missingItems = [];
        originalQuantities.forEach((quantity, variantId) => {
          const missingQuantity = quantity - (currentQuantities.get(variantId) || 0);
          if (missingQuantity <= 0) return;
          const originalLine = originalSnapshot.find((line) => line.variantId === variantId);
          if (!originalLine) return;
          const item = {
            id: variantId,
            quantity: missingQuantity,
            properties: { ...(originalLine.properties || {}) }
          };
          if (originalLine.sellingPlan) item.selling_plan = originalLine.sellingPlan;
          missingItems.push(item);
        });

        if (missingItems.length) {
          const addResponse = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ items: missingItems })
          });
          if (!addResponse.ok) return false;
        }

        cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) return false;
        cart = await cartResponse.json();
        currentSnapshot = this.snapshotBundle(cart);
        const updates = this.buildGroupQuantityUpdates(currentSnapshot, originalQuantities);
        if (updates.missingVariantIds.length) return false;

        if (Object.keys(updates.values).length) {
          const updateResponse = await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ updates: updates.values })
          });
          if (!updateResponse.ok) return false;
        }

        const verificationResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!verificationResponse.ok) return false;
        const verifiedCart = await verificationResponse.json();
        return this.strictSnapshotsMatch(this.snapshotBundle(verifiedCart), originalSnapshot);
      } catch (error) {
        return false;
      }
    }

    createBundleId() {
      const randomPart = typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      return `TOFFEE-${this.dataset.hostProductId || 'SET'}-${randomPart}`;
    }

    async rollbackBundle(bundleId) {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) return false;
        const cart = await cartResponse.json();
        const updates = {};
        (cart.items || []).forEach((item) => {
          if (item.properties?._toffee_bundle_id === bundleId && item.key) updates[item.key] = 0;
        });
        if (Object.keys(updates).length === 0) return true;

        const updateResponse = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ updates })
        });
        if (!updateResponse.ok) return false;

        const verificationResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!verificationResponse.ok) return false;
        const verifiedCart = await verificationResponse.json();
        return !(verifiedCart.items || []).some((item) => item.properties?._toffee_bundle_id === bundleId);
      } catch (error) {
        return false;
      }
    }

    async refreshSelectedAvailability() {
      const selectedItems = Array.from(this.selection.values());
      const root = window.Shopify?.routes?.root || '/';

      await Promise.allSettled(selectedItems.map(async (item) => {
        if (!item.handle) return;
        const response = await fetch(`${root}products/${encodeURIComponent(item.handle)}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const productJson = await response.json();
        const variant = (productJson.variants || []).find((candidate) => Number(candidate.id) === item.variantId);
        if (!variant) return;

        item.available = Boolean(variant.available);
        item.price = priceToCents(variant.price, item.price);
        if (variant.inventory_quantity != null) {
          item.inventoryManagement = variant.inventory_management || item.inventoryManagement;
          item.inventoryPolicy = variant.inventory_policy || item.inventoryPolicy;
          item.inventoryQuantity = Number.parseInt(variant.inventory_quantity, 10);
          item.maxQuantity = inventoryCap(item.inventoryManagement, item.inventoryPolicy, item.inventoryQuantity);
        }
        const catalogItem = this.products.get(item.variantId);
        if (catalogItem) {
          catalogItem.available = item.available;
          catalogItem.price = item.price;
          catalogItem.inventoryManagement = item.inventoryManagement;
          catalogItem.inventoryPolicy = item.inventoryPolicy;
          catalogItem.inventoryQuantity = item.inventoryQuantity;
          catalogItem.maxQuantity = item.maxQuantity;
          catalogItem.card.dataset.available = String(item.available);
        }
      }));

      this.saveSession();
      this.syncCards();
      this.render();
    }

    emitCartEvents(responseJson, cart) {
      this.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: responseJson.items || responseJson, cart }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart }
      }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      window.requestAnimationFrame(() => this.openImpactCartDrawer());
    }

    emitCartUpdateEvents(cart) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'cart:update', cart }
      }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      window.requestAnimationFrame(() => this.openImpactCartDrawer());
    }

    openImpactCartDrawer() {
      const drawer = document.querySelector('cart-drawer, cart-notification-drawer, [data-cart-drawer]');
      if (!drawer || drawer.hasAttribute('open') || drawer.classList.contains('is-open')) return;

      if (typeof drawer.show === 'function') {
        drawer.show();
      } else if (typeof drawer.open === 'function') {
        drawer.open();
      }
    }

    suppressStickyForCart() {
      this.cartSuppressed = true;
      this.updateStickyVisibility();
      window.clearTimeout(this.cartSuppressionTimer);
      this.cartSuppressionTimer = window.setTimeout(() => {
        this.cartSuppressed = false;
        this.updateStickyVisibility();
      }, 2600);
    }

    setBusy(busy) {
      this.busy = busy;
      this.primaryButtons.forEach((button) => {
        button.disabled = busy;
        button.setAttribute('aria-busy', String(busy));
        if (busy) button.textContent = this.editGroupLoaded ? 'Set wird aktualisiert…' : 'Set wird hinzugefügt…';
      });
      this.syncCards();
      if (!busy) this.render();
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
      this.live(message);
      this.errorElement.scrollIntoView({ block: 'nearest', behavior: this.reducedMotion() ? 'auto' : 'smooth' });
    }

    live(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      window.requestAnimationFrame(() => {
        this.liveRegion.textContent = message;
      });
    }

    reducedMotion() {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define(ELEMENT_NAME, ToffeeSetBuilder);
})();
